const SWISSTOPO_SWISSIMAGE_TILE_URL = 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857';
const OPENSTREETMAP_TILE_URL = 'https://tile.openstreetmap.org';
const SWISSTOPO_SEARCH_URL = 'https://api3.geo.admin.ch/rest/services/ech/SearchServer';
const SATELLITE_TILE_SIZE_PX = 256;
const SATELLITE_MIN_ZOOM = 10;
const SATELLITE_TILE_MAX_ZOOM = 20;
const NORMAL_MAP_TILE_MAX_ZOOM = 19;
const SATELLITE_MAX_ZOOM = 22;
const SATELLITE_MAP_MODES = Object.freeze({
  SATELLITE: 'satellite',
  MAP: 'map'
});
const SWISSTOPO_SWISSIMAGE_BOUNDS = Object.freeze({
  minLat: 45.3,
  maxLat: 48.4,
  minLng: 5.0,
  maxLng: 11.6
});
const WEB_MERCATOR_LAT_LIMIT = 85.05112878;
const EARTH_RADIUS_M = 6371008.8;
const WEB_MERCATOR_RADIUS_M = 6378137;

function clampLatitude(lat) {
  const numeric = Number(lat);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(-WEB_MERCATOR_LAT_LIMIT, Math.min(WEB_MERCATOR_LAT_LIMIT, numeric));
}

function normalizeLongitude(lng) {
  const numeric = Number(lng);
  if (!Number.isFinite(numeric)) return 0;
  let next = numeric;
  while (next < -180) next += 360;
  while (next > 180) next -= 360;
  return next;
}

function clampSatelliteZoom(zoom) {
  const numeric = Math.round(Number(zoom));
  if (!Number.isFinite(numeric)) return 18;
  return Math.max(SATELLITE_MIN_ZOOM, Math.min(SATELLITE_MAX_ZOOM, numeric));
}

function clampSatelliteTileZoom(zoom) {
  const numeric = Math.round(Number(zoom));
  if (!Number.isFinite(numeric)) return 18;
  return Math.max(SATELLITE_MIN_ZOOM, Math.min(SATELLITE_TILE_MAX_ZOOM, numeric));
}

function normalizeSatelliteMapMode(mode) {
  const value = String(mode || '').trim().toLowerCase();
  return value === SATELLITE_MAP_MODES.MAP
    ? SATELLITE_MAP_MODES.MAP
    : SATELLITE_MAP_MODES.SATELLITE;
}

function satelliteMapTileMaxZoom(mode) {
  return normalizeSatelliteMapMode(mode) === SATELLITE_MAP_MODES.MAP
    ? NORMAL_MAP_TILE_MAX_ZOOM
    : SATELLITE_TILE_MAX_ZOOM;
}

function satelliteMapAttribution(mode) {
  return normalizeSatelliteMapMode(mode) === SATELLITE_MAP_MODES.MAP
    ? '© OpenStreetMap contributors'
    : 'swisstopo SWISSIMAGE';
}

function satelliteScaleForZoom(zoom) {
  return SATELLITE_TILE_SIZE_PX * (2 ** clampSatelliteZoom(zoom));
}

function lonLatToWorldPixel(lat, lng, zoom) {
  const scale = satelliteScaleForZoom(zoom);
  const safeLat = clampLatitude(lat);
  const safeLng = normalizeLongitude(lng);
  const sinLat = Math.sin((safeLat * Math.PI) / 180);
  return {
    x: ((safeLng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale
  };
}

function worldPixelToLonLat(x, y, zoom) {
  const scale = satelliteScaleForZoom(zoom);
  const lng = (Number(x) / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * Number(y)) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return {
    lat: clampLatitude(lat),
    lng: normalizeLongitude(lng)
  };
}

function swisstopoSwissimageTileUrl(zoom, tileX, tileY) {
  const z = clampSatelliteTileZoom(zoom);
  const tiles = 2 ** z;
  const x = ((Math.floor(Number(tileX)) % tiles) + tiles) % tiles;
  const y = Math.max(0, Math.min(tiles - 1, Math.floor(Number(tileY))));
  return `${SWISSTOPO_SWISSIMAGE_TILE_URL}/${z}/${x}/${y}.jpeg`;
}

function swisstopoSwissimageTileAvailable(zoom, tileX, tileY) {
  const z = clampSatelliteTileZoom(zoom);
  const tiles = 2 ** z;
  const x = Math.floor(Number(tileX));
  const y = Math.floor(Number(tileY));
  if (!Number.isFinite(x) || !Number.isFinite(y) || y < 0 || y >= tiles) return false;
  const topLeft = worldPixelToLonLat(x * SATELLITE_TILE_SIZE_PX, y * SATELLITE_TILE_SIZE_PX, z);
  const bottomRight = worldPixelToLonLat((x + 1) * SATELLITE_TILE_SIZE_PX, (y + 1) * SATELLITE_TILE_SIZE_PX, z);
  const minLat = Math.min(topLeft.lat, bottomRight.lat);
  const maxLat = Math.max(topLeft.lat, bottomRight.lat);
  const minLng = Math.min(topLeft.lng, bottomRight.lng);
  const maxLng = Math.max(topLeft.lng, bottomRight.lng);
  return maxLat >= SWISSTOPO_SWISSIMAGE_BOUNDS.minLat
    && minLat <= SWISSTOPO_SWISSIMAGE_BOUNDS.maxLat
    && maxLng >= SWISSTOPO_SWISSIMAGE_BOUNDS.minLng
    && minLng <= SWISSTOPO_SWISSIMAGE_BOUNDS.maxLng;
}

function openStreetMapTileUrl(zoom, tileX, tileY) {
  const z = Math.max(SATELLITE_MIN_ZOOM, Math.min(NORMAL_MAP_TILE_MAX_ZOOM, Math.round(Number(zoom) || 18)));
  const tiles = 2 ** z;
  const x = ((Math.floor(Number(tileX)) % tiles) + tiles) % tiles;
  const y = Math.max(0, Math.min(tiles - 1, Math.floor(Number(tileY))));
  return `${OPENSTREETMAP_TILE_URL}/${z}/${x}/${y}.png`;
}

function openStreetMapTileAvailable(zoom, tileX, tileY) {
  const z = Math.max(SATELLITE_MIN_ZOOM, Math.min(NORMAL_MAP_TILE_MAX_ZOOM, Math.round(Number(zoom) || 18)));
  const tiles = 2 ** z;
  const x = Math.floor(Number(tileX));
  const y = Math.floor(Number(tileY));
  return Number.isFinite(x) && Number.isFinite(y) && y >= 0 && y < tiles;
}

function satelliteMapTileUrl(mode, zoom, tileX, tileY) {
  return normalizeSatelliteMapMode(mode) === SATELLITE_MAP_MODES.MAP
    ? openStreetMapTileUrl(zoom, tileX, tileY)
    : swisstopoSwissimageTileUrl(zoom, tileX, tileY);
}

function satelliteMapTileAvailable(mode, zoom, tileX, tileY) {
  return normalizeSatelliteMapMode(mode) === SATELLITE_MAP_MODES.MAP
    ? openStreetMapTileAvailable(zoom, tileX, tileY)
    : swisstopoSwissimageTileAvailable(zoom, tileX, tileY);
}

function lonLatToWebMercatorMeters(lat, lng) {
  const safeLat = clampLatitude(lat);
  const safeLng = normalizeLongitude(lng);
  return {
    x: WEB_MERCATOR_RADIUS_M * ((safeLng * Math.PI) / 180),
    y: WEB_MERCATOR_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + ((safeLat * Math.PI) / 180) / 2))
  };
}

function haversineDistanceMeters(a, b) {
  const lat1 = (Number(a?.lat) || 0) * Math.PI / 180;
  const lat2 = (Number(b?.lat) || 0) * Math.PI / 180;
  const dLat = lat2 - lat1;
  const dLng = ((Number(b?.lng) || 0) - (Number(a?.lng) || 0)) * Math.PI / 180;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function normalizeSatelliteCorner(corner) {
  return {
    lat: clampLatitude(corner?.lat),
    lng: normalizeLongitude(corner?.lng)
  };
}

function satelliteSelectionMetrics(corners) {
  const northWest = normalizeSatelliteCorner(corners?.northWest);
  const northEast = normalizeSatelliteCorner(corners?.northEast);
  const southWest = normalizeSatelliteCorner(corners?.southWest);
  const southEast = normalizeSatelliteCorner(corners?.southEast);
  const northWidthM = haversineDistanceMeters(northWest, northEast);
  const southWidthM = haversineDistanceMeters(southWest, southEast);
  const westHeightM = haversineDistanceMeters(northWest, southWest);
  const eastHeightM = haversineDistanceMeters(northEast, southEast);
  return {
    northWest,
    northEast,
    southWest,
    southEast,
    realWidthMm: Math.max(1, Math.round(((northWidthM + southWidthM) / 2) * 1000)),
    realHeightMm: Math.max(1, Math.round(((westHeightM + eastHeightM) / 2) * 1000))
  };
}

function satelliteImageSizeForRealSize(realWidthMm, realHeightMm, maxSizePx = 8192) {
  const width = Math.max(1, Number(realWidthMm) || 1);
  const height = Math.max(1, Number(realHeightMm) || 1);
  const maxPx = Math.max(512, Math.min(8192, Math.round(Number(maxSizePx) || 8192)));
  if (width >= height) {
    return {
      imageWidthPx: maxPx,
      imageHeightPx: Math.max(256, Math.round(maxPx * (height / width)))
    };
  }
  return {
    imageWidthPx: Math.max(256, Math.round(maxPx * (width / height))),
    imageHeightPx: maxPx
  };
}

function satelliteFloorplanImageUrl(floorplan = {}) {
  return swisstopoSwissimageTileUrl(
    Number(floorplan.tileZoom) || SATELLITE_MAX_ZOOM,
    Number(floorplan.tileX) || 0,
    Number(floorplan.tileY) || 0
  );
}

function createSatelliteFloorplan(corners, options = {}) {
  const metrics = satelliteSelectionMetrics(corners);
  const imageSize = satelliteImageSizeForRealSize(
    metrics.realWidthMm,
    metrics.realHeightMm,
    options.maxImageSizePx
  );
  const floorplan = {
    kind: 'satellite',
    backgroundType: 'satellite',
    provider: options.provider || 'swisstopo',
    mapMode: normalizeSatelliteMapMode(options.mapMode),
    name: options.name || 'Satellitenkarte (swisstopo SWISSIMAGE)',
    northWestLat: metrics.northWest.lat,
    northWestLng: metrics.northWest.lng,
    northEastLat: metrics.northEast.lat,
    northEastLng: metrics.northEast.lng,
    southWestLat: metrics.southWest.lat,
    southWestLng: metrics.southWest.lng,
    southEastLat: metrics.southEast.lat,
    southEastLng: metrics.southEast.lng,
    realWidthMm: metrics.realWidthMm,
    realHeightMm: metrics.realHeightMm,
    imageWidthPx: imageSize.imageWidthPx,
    imageHeightPx: imageSize.imageHeightPx,
    x: Number.isFinite(Number(options.x)) ? Number(options.x) : -metrics.realWidthMm / 2,
    z: Number.isFinite(Number(options.z)) ? Number(options.z) : -metrics.realHeightMm / 2,
    width: metrics.realWidthMm,
    height: metrics.realHeightMm,
    opacity: Number.isFinite(Number(options.opacity)) ? Number(options.opacity) : 0.85,
    visible: options.visible !== false,
    locked: options.locked !== false
  };
  floorplan.url = satelliteFloorplanImageUrl(floorplan);
  return floorplan;
}

function swisstopoSearchUrl(query, maxLocations = 6) {
  const params = new URLSearchParams({
    searchText: String(query || ''),
    type: 'locations',
    origins: 'address,zipcode,gg25,gazetteer',
    limit: String(Math.max(1, Math.min(12, Math.round(Number(maxLocations) || 6))))
  });
  return `${SWISSTOPO_SEARCH_URL}?${params.toString()}`;
}

export {
  NORMAL_MAP_TILE_MAX_ZOOM,
  OPENSTREETMAP_TILE_URL,
  SATELLITE_MAP_MODES,
  SATELLITE_MAX_ZOOM,
  SATELLITE_MIN_ZOOM,
  SATELLITE_TILE_MAX_ZOOM,
  SATELLITE_TILE_SIZE_PX,
  SWISSTOPO_SEARCH_URL,
  SWISSTOPO_SWISSIMAGE_BOUNDS,
  SWISSTOPO_SWISSIMAGE_TILE_URL,
  clampSatelliteZoom,
  clampSatelliteTileZoom,
  createSatelliteFloorplan,
  haversineDistanceMeters,
  lonLatToWebMercatorMeters,
  lonLatToWorldPixel,
  normalizeSatelliteMapMode,
  normalizeSatelliteCorner,
  satelliteMapAttribution,
  satelliteFloorplanImageUrl,
  satelliteImageSizeForRealSize,
  satelliteMapTileAvailable,
  satelliteMapTileMaxZoom,
  satelliteMapTileUrl,
  satelliteSelectionMetrics,
  swisstopoSwissimageTileAvailable,
  swisstopoSwissimageTileUrl,
  swisstopoSearchUrl,
  worldPixelToLonLat
};
